import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isRetryable } from '../content/errors';
import { weekStartOf } from '../content/group-progress';
import { attemptScope, type CompleteExerciseInput } from '../content/outbox';
import { ensurePhotoUploaded } from '../content/photo';
import { loadSessions, recordAttempt, sessionOn } from '../content/sessions';
import { deriveSummary, writeSummary } from '../content/summary';
import { deviceTimeZone } from '../domain/day';
import { queryKeys } from '../query/keys';
import { enqueueAttempt } from './outbox-store';
import { useConfig } from './provider';
import { useWriteScope } from './use-global-group';
import { marksForSummary } from './use-marks';
import { useMe } from './use-me';
import { useSessions } from './use-sessions';
import { todayIso } from './use-today';

export type { CompleteExerciseInput } from '../content/outbox';

/**
 * "Done": save the attempt in today's session (creating the session on the first exercise
 * of the day), then refresh the derived summary. Without provisioned types the mutation
 * resolves without saving, so the screens behave the same everywhere. When Core cannot be
 * reached the attempt goes to the outbox instead of being lost and is sent again when the
 * connection is back (`useFlushOutbox`). Written as a member of the group where the person is in it
 * (`useWriteScope`), into the day's row written the same way.
 */
export function useCompleteExercise() {
  const queryClient = useQueryClient();
  const { strongerMemoryTypes: types, strongerMemoryGroupActorUuid: groupActorUuid } = useConfig();
  const sessions = useSessions();
  const me = useMe();
  const writeScope = useWriteScope();
  return useMutation({
    mutationFn: async (input: CompleteExerciseInput) => {
      if (!types) return undefined;
      const date = todayIso();
      const timeZone = deviceTimeZone();
      let recorded: Awaited<ReturnType<typeof recordAttempt>>;
      // What goes to the outbox on failure: the photo's Core uuid once uploaded, else the photo.
      let sent = input;
      try {
        const scope = attemptScope(input, await writeScope());
        const today = sessionOn(sessions.data ?? [], date, scope);
        sent = await ensurePhotoUploaded(input, {
          subjectUuid: me.data?.uuid,
          scope,
          sessionLineageUuid: today?.lineageUuid,
        });
        recorded = await recordAttempt(types, {
          ...sent,
          date,
          timeZone,
          groupActorUuid,
          scope,
          subjectUuid: me.data?.uuid,
          sessionUuid: today?.uuid,
          sessionLineageUuid: today?.lineageUuid,
        });
      } catch (error) {
        if (isRetryable(error)) {
          enqueueAttempt({
            id: `${date}-${input.kind}-${input.completedAt}`,
            date,
            timeZone,
            input: sent,
            owner: me.data?.uuid,
          });
        }
        throw error;
      }
      // Best effort: the summary is derived data; a failure here must not undo "Done".
      try {
        const fresh = await loadSessions(types);
        queryClient.setQueryData(queryKeys.sessions(), fresh);
        const marks = await marksForSummary(
          queryClient,
          { strongerMemoryTypes: types, strongerMemoryGroupActorUuid: groupActorUuid },
          me.data?.uuid,
        );
        await writeSummary(
          types,
          deriveSummary(fresh, date, groupActorUuid, marks),
          await writeScope(),
          me.data?.uuid,
        );
        await queryClient.invalidateQueries({
          queryKey: queryKeys.groupProgress(weekStartOf(date)),
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.groupDashboards() });
      } catch {
        // Next completion recomputes it.
      }
      return recorded;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.sessions() }),
  });
}
